import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import Joyride, { type CallBackProps, STATUS, EVENTS, ACTIONS } from 'react-joyride';
import { useAuthStore, useUIStore, useOnboardingStore } from '@vinum/shared';
import { TourTooltip } from './TourTooltip';
import { TOUR_STEPS, MOBILE_TOUR_STEPS } from './tourSteps';

export const OnboardingTour: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const updateUser = useAuthStore((s) => s.updateUser);
  const breakpoint = useUIStore((s) => s.breakpoint);
  const isMobile = breakpoint === 'small' || breakpoint === 'mobile';

  const isRunning = useOnboardingStore((s) => s.isRunning);
  const stepIndex = useOnboardingStore((s) => s.stepIndex);
  const setStepIndex = useOnboardingStore((s) => s.setStepIndex);
  const completeTour = useOnboardingStore((s) => s.completeTour);
  const stopTour = useOnboardingStore((s) => s.stopTour);

  const steps = useMemo(() => (isMobile ? MOBILE_TOUR_STEPS : TOUR_STEPS), [isMobile]);
  const advancingRef = useRef(false);

  // Current step metadata
  const currentStep = steps[stepIndex];
  const isInteractiveStep = !!(currentStep?.data as any)?.interactive;

  /**
   * Skip forward intelligently: if the current step is interactive,
   * skip both it AND the following "explain" step (they always come in pairs).
   */
  const skipMissingTarget = useCallback(
    (fromIndex: number) => {
      const stepData = steps[fromIndex]?.data as any;
      const isInteractive = !!stepData?.interactive;
      // Interactive steps are always followed by an explain step → skip 2
      const skipTo = isInteractive ? fromIndex + 2 : fromIndex + 1;

      if (skipTo < steps.length) {
        setStepIndex(skipTo);
      } else {
        // No more steps — complete the tour
        if (user) {
          completeTour(user.id);
          updateUser({ ...user, tutorialCompleted: true });
        }
      }
    },
    [steps, setStepIndex, user, completeTour, updateUser]
  );

  // Listen for clicks on the target element for interactive steps.
  // Let the click go through (real navigation), then advance the step after a delay.
  useEffect(() => {
    if (!isRunning || !isInteractiveStep || !currentStep) return;

    const target = currentStep.target;
    if (typeof target !== 'string' || target === 'body') return;

    const handleClick = (e: MouseEvent) => {
      if (advancingRef.current) return;

      const el = document.querySelector(target);
      if (!el) return;

      // Check if the click is on the target element or inside it
      if (el.contains(e.target as Node)) {
        // Do NOT preventDefault — let React Router navigate naturally
        advancingRef.current = true;

        // Advance to next step after a delay so the new page has time to render
        setTimeout(() => {
          if (stepIndex < steps.length - 1) {
            setStepIndex(stepIndex + 1);
          }
          advancingRef.current = false;
        }, 700);
      }
    };

    // Use capturing phase to detect the click, but don't block it
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [isRunning, isInteractiveStep, currentStep, stepIndex, steps.length, setStepIndex]);

  // Auto-skip steps whose target element doesn't exist in the DOM.
  // For interactive steps, skip the pair (interactive + explain).
  useEffect(() => {
    if (!isRunning || !currentStep) return;

    const target = currentStep.target;
    if (typeof target !== 'string' || target === 'body') return;

    const timer = setTimeout(() => {
      const el = document.querySelector(target);
      if (!el) {
        skipMissingTarget(stepIndex);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [isRunning, currentStep, stepIndex, skipMissingTarget]);

  const handleCallback = useCallback(
    (data: CallBackProps) => {
      const { action, index, status, type } = data;
      const stepData = data.step?.data as any;
      const isInteractive = !!stepData?.interactive;

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        if (user) {
          completeTour(user.id);
          updateUser({ ...user, tutorialCompleted: true });
        }
        return;
      }

      // Handle target not found — skip step (pair if interactive)
      if (type === EVENTS.TARGET_NOT_FOUND) {
        skipMissingTarget(index);
        return;
      }

      // For non-interactive steps, handle Next/Back buttons normally
      if (type === EVENTS.STEP_AFTER && !isInteractive) {
        if (action === ACTIONS.NEXT) {
          setStepIndex(index + 1);
        } else if (action === ACTIONS.PREV) {
          setStepIndex(index - 1);
        }
      }

      if (action === ACTIONS.CLOSE) {
        stopTour();
      }
    },
    [user, completeTour, updateUser, setStepIndex, stopTour, skipMissingTarget]
  );

  if (!isRunning) return null;

  return (
    <Joyride
      steps={steps}
      stepIndex={stepIndex}
      run={isRunning}
      continuous
      showSkipButton
      showProgress={false}
      disableOverlayClose
      disableScrolling={false}
      tooltipComponent={TourTooltip}
      callback={handleCallback}
      styles={{
        options: {
          zIndex: 10000,
          arrowColor: 'var(--color-card, #ffffff)',
        },
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
      }}
      locale={{
        back: 'Indietro',
        close: 'Chiudi',
        last: 'Inizia!',
        next: 'Avanti',
        skip: 'Salta',
      }}
    />
  );
};
